import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { FiGrid, FiCheckCircle, FiClock, FiAlertTriangle, FiDownload } from "react-icons/fi";
import MainCard from "../../../component/MainCard/MainCard";
import * as actions from "../../../Redux/Actions";
import { showErrorToastMessage } from "../../../component/Toasts/Toasts";
import hanldeUnauthorizedUser from "../../../component/AccessConditions/UnauthorizedUser";
import { downloadSuccessorReportPdf } from "../report/successorReportPdf";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import "../readable-form.scss";

const empName = (e: any) =>
  [e?.candidateName, e?.middleName, e?.lastname].filter(Boolean).join(" ").trim() || "-";

const buHeadLabel = (e: any) => {
  const parts = [e?.candidateName, e?.middleName, e?.lastname].filter(Boolean).join(" ").trim();
  return e?.employeeId ? `${parts}-${e.employeeId}` : parts;
};

// Brand palette for the feedback modal (matches the PDF report).
const NAVY = "#1f3a5f";
const ORANGE = "#e8832a";
const LIGHT = "#f5f7fa";
const BORDER = "#e5e9ef";
const MUTED = "#78808a";

// A labelled section header with an orange accent bar — used in the feedback modal.
const modalSectionHead = (label: string) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 10px" }}>
    <span style={{ width: 4, height: 18, background: ORANGE, borderRadius: 2, display: "inline-block" }} />
    <span style={{ color: NAVY, fontWeight: 700, fontSize: 15 }}>{label}</span>
  </div>
);

// The feedback modal body: progress feedback (§C) + the 5 qualitative boxes (§B),
// styled as cards so it reads cleanly instead of a wall of text.
const renderFeedbackModal = (cand: any, qual: any[] | null | undefined) => {
  const progress = (cand?.developmentProgressFeedback || "").trim();
  const filled = (qual || []).filter((q: any) => (q.text || "").trim());
  return (
    <div style={{ padding: "4px 2px" }}>
      {/* Progress feedback */}
      {modalSectionHead("Progress Feedback")}
      <div
        style={{
          background: LIGHT,
          borderLeft: `4px solid ${ORANGE}`,
          borderRadius: 6,
          padding: "10px 14px",
          marginBottom: 20,
        }}
      >
        {progress ? (
          <>
            <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.55, color: "#212529" }}>{progress}</p>
            {cand.developmentProgressFeedbackAt && (
              <p style={{ margin: "6px 0 0", fontSize: 11.5, color: MUTED }}>
                as of {new Date(cand.developmentProgressFeedbackAt).toLocaleDateString()}
                {cand.developmentProgressFeedbackBy ? ` · ${cand.developmentProgressFeedbackBy}` : ""}
              </p>
            )}
          </>
        ) : (
          <span style={{ color: MUTED, fontStyle: "italic" }}>Not recorded yet</span>
        )}
      </div>

      {/* Qualitative feedback — the 5 boxes */}
      {modalSectionHead("Qualitative Feedback")}
      {qual == null ? (
        <p style={{ color: MUTED, margin: 0 }}>Loading…</p>
      ) : filled.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filled.map((q: any) => (
            <div
              key={q.param}
              style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden" }}
            >
              <div
                style={{
                  background: NAVY,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                  padding: "7px 14px",
                }}
              >
                {q.label}
              </div>
              <p
                style={{
                  margin: 0,
                  padding: "10px 14px",
                  whiteSpace: "pre-wrap",
                  textAlign: "justify",
                  lineHeight: 1.6,
                  color: "#212529",
                }}
              >
                {q.text}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <span style={{ color: MUTED, fontStyle: "italic" }}>Not recorded yet</span>
      )}
    </div>
  );
};

// HR records High/Medium/Low; the grid works in 1-3. Same mapping the source
// workbook uses (Low Performance (1) ... High Performance (3)).
const LEVEL_SCORE: any = { High: 3, Medium: 2, Low: 1 };

// Box = (Potential x 3) - (3 - Performance) -- the workbook's own formula, verified
// against every row of its Employee_Data sheet.
const boxOf = (performance: string, potential: string) => {
  const p = LEVEL_SCORE[performance];
  const pot = LEVEL_SCORE[potential];
  if (!p || !pot) return null; // not assessed on both axes yet
  return pot * 3 - (3 - p);
};

/**
 * Box -> Category. The source workbook's nine labels, corrected onto the right cells.
 *
 * Its lookup was written as though a higher box number meant a better person, but the
 * formula weights Potential x3, so box order isn't a ranking — the labels had drifted
 * off their cells. It called Performance 1 + Potential 3 a "Strong Performer" (its own
 * data has Punanjay, Performance 1, reading as one) and Performance 3 + Potential 1 a
 * "Low Performer". Two swaps fix it: 6<->7 and 3<->4. Same nine words, right cells.
 *
 * Open with HR: box 2 (Performance 2 + Potential 1) is "Inconsistent", which describes
 * someone erratic — but this cell is a steady middling performer with little headroom.
 * Left as hers rather than renamed on a guess.
 */
const BOX_CATEGORY: any = {
  9: "Future Leader", //      perf 3, pot 3
  8: "High Potential", //     perf 2, pot 3
  7: "Emerging Potential", // perf 1, pot 3 -- high potential, not performing yet
  6: "Strong Performer", //   perf 3, pot 2 -- performs now, some headroom
  5: "Core Talent", //        perf 2, pot 2
  4: "Low Performer", //      perf 1, pot 2
  3: "Specialist", //         perf 3, pot 1 -- excellent at the job, won't grow
  2: "Inconsistent", //       perf 2, pot 1 -- weakest label, see above
  1: "At Risk", //            perf 1, pot 1
};
// Colour follows the source workbook exactly: only the three DIAGONAL boxes are
// tinted (box 9 green, box 5 yellow, box 1 red) — its traffic light where
// Performance and Potential agree. The other six cells stay plain.
const bandOf = (box: number) => {
  if (box === 9) return "green";
  if (box === 5) return "amber";
  if (box === 1) return "red";
  return "plain";
};

// Labels as the source workbook writes them.
const POTENTIAL_ROWS = [
  { score: 3, label: "High Potential (3)" },
  { score: 2, label: "Medium Potential (2)" },
  { score: 1, label: "Low Potential (1)" },
];
const PERFORMANCE_COLS = [
  { score: 1, label: "Low Performance (1)" },
  { score: 2, label: "Medium Performance (2)" },
  { score: 3, label: "High Performance (3)" },
];

/**
 * Succession dashboard — designation-level, not employee-level.
 *
 * It answers "which critical roles are exposed?", not "how is one person doing?".
 * The 9-box holds COUNTS (as the source workbook does); clicking a cell reveals who
 * is in it. An individual only appears on the drill-down.
 */
const Dashboard = () => {
  const { unAuthorizeUser } = hanldeUnauthorizedUser();
  const dispatch = useDispatch();

  const [roles, setRoles] = useState<any[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState<any>(null);
  const [buHeadFilterSel, setBuHeadFilterSel] = useState<any>(null);
  const [activeBox, setActiveBox] = useState<number | null>(null);
  // the candidate whose feedback is shown in the detail modal (null = closed)
  const [detailCand, setDetailCand] = useState<any>(null);
  // candidate id whose report is currently being built
  const [reportId, setReportId] = useState<string | null>(null);
  // lazily-loaded qualitative feedback (the 5 boxes) per candidate; undefined = not
  // fetched, null = loading, array = loaded
  const [qualMap, setQualMap] = useState<Record<string, any[] | null>>({});

  // open the feedback modal for a candidate; fetch the 5 qualitative boxes on first open
  const openDetails = (c: any) => {
    setDetailCand(c);
    if (qualMap[c._id] === undefined) {
      setQualMap((m) => ({ ...m, [c._id]: null }));
      dispatch(
        actions.getSuccessorReportAction(c._id, (res: any) => {
          const q = res?.data?.qualitative;
          setQualMap((m) => ({ ...m, [c._id]: Array.isArray(q) ? q : [] }));
        })
      );
    }
  };

  const downloadReport = (candidateId: string) => {
    if (!candidateId || reportId) return;
    setReportId(candidateId);
    dispatch(
      actions.getSuccessorReportAction(candidateId, (res: any) => {
        setReportId(null);
        if (res?.error || !res?.data) {
          showErrorToastMessage("Couldn't build the report. Please retry.");
          return;
        }
        try {
          downloadSuccessorReportPdf(res.data);
        } catch (e) {
          showErrorToastMessage("Couldn't generate the PDF.");
        }
      })
    );
  };

  useEffect(() => {
    unAuthorizeUser();
    dispatch(
      actions.getAllCriticalRolesAction((res: any) => {
        if (res?.error) showErrorToastMessage("Couldn't load the dashboard. Please retry.");
        else if (res?.data) setRoles(res.data);
      })
    );
    dispatch(
      actions.getCandidatesByCriticalRoleAction(undefined, (res: any) => {
        if (res?.error) showErrorToastMessage("Couldn't load successors for the dashboard.");
        else setCandidates(res?.data || []);
        setLoading(false);
      })
    );
  }, []);

  const roleOptions = [
    { value: "", label: "All roles" },
    ...roles
      .filter((r: any) => r.designation?.title)
      .map((r: any) => ({
        value: r._id,
        label: r.client?.clientName
          ? `${r.designation.title} — ${r.client.clientName}`
          : r.designation.title,
      })),
  ];

  const selectedRole = roleFilter?.value || "";
  const buHeadFilter = buHeadFilterSel?.value || "";

  // BU Head filter, driven by the roles' buHeadRef (a role -> buHeadRef map plus the
  // distinct BU heads for the dropdown). Applied to both candidate-level (9-box) and
  // role-level (status) views so the whole dashboard scopes together.
  const roleToBuHead: Record<string, string> = {};
  roles.forEach((r: any) => {
    if (r.buHeadRef) roleToBuHead[r._id] = r.buHeadRef;
  });
  const buHeadOptions = [
    { value: "", label: "All BU heads" },
    ...Array.from(
      roles.reduce((m: Map<string, string>, r: any) => {
        if (r.buHeadRef && !m.has(r.buHeadRef)) m.set(r.buHeadRef, buHeadLabel(r.buHead) || r.buHeadRef);
        return m;
      }, new Map<string, string>())
    ).map(([value, label]) => ({ value, label })),
  ];

  const matchScope = (roleRef: string) =>
    (!selectedRole || roleRef === selectedRole) &&
    (!buHeadFilter || roleToBuHead[roleRef] === buHeadFilter);

  // Scoping is enforced server-side (a manager's fetch only ever returns their
  // own reportees), so no client-side team filter is needed here.
  const scoped = candidates.filter((c) => matchScope(c.criticalRoleRef));

  // ---- 9-box counts -------------------------------------------------------
  const countIn = (perf: number, pot: number) =>
    scoped.filter(
      (c) => LEVEL_SCORE[c.performance] === perf && LEVEL_SCORE[c.potential] === pot
    ).length;

  const plotted = scoped.filter((c) => boxOf(c.performance, c.potential) !== null);
  const unplotted = scoped.length - plotted.length;

  // ---- designation-level status ------------------------------------------
  const scopedRoles = roles.filter((r) => matchScope(r._id));

  // The blueprint's three designation-level states: "how many roles are covered, in
  // progress, or at risk". Covered = someone could step in today; In Progress =
  // successors named but none ready yet; At Risk = nobody named at all.
  const stateOf = (successors: number, readyNow: number) => {
    if (readyNow > 0) return "Covered";
    if (successors > 0) return "In Progress";
    return "At Risk";
  };

  const roleStats = scopedRoles.map((r: any) => {
    const mine = candidates.filter((c) => c.criticalRoleRef === r._id);
    const readyNow = mine.filter((c) => c.readiness === "Ready Now").length;
    return {
      role: r,
      successors: mine.length,
      readyNow,
      state: stateOf(mine.length, readyNow),
    };
  });

  const covered = roleStats.filter((s) => s.state === "Covered").length;
  const inProgress = roleStats.filter((s) => s.state === "In Progress").length;
  const atRisk = roleStats.filter((s) => s.state === "At Risk").length;
  const coveragePct = roleStats.length
    ? Math.round((covered / roleStats.length) * 100)
    : 0;

  const drill = activeBox
    ? scoped.filter((c) => boxOf(c.performance, c.potential) === activeBox)
    : [];

  return (
    <MainCard>
      <div className="assessment-heading-button-wrapper">
        <div>
          <h2>Succession Dashboard</h2>
        </div>
      </div>

      <div className="dp-filters readable-form mt-3">
        <div className="dp-filter">
          <label className="form-label">Critical Role</label>
          <Select
            className="dropdown-select"
            isSearchable={true}
            isClearable={true}
            placeholder="All roles"
            value={roleFilter}
            options={roleOptions}
            onChange={(sel: any) => {
              setRoleFilter(sel);
              setActiveBox(null);
            }}
            noOptionsMessage={() => "No critical roles yet"}
          />
        </div>
        <div className="dp-filter">
          <label className="form-label">BU Head</label>
          <Select
            className="dropdown-select"
            isSearchable={true}
            isClearable={true}
            placeholder="All BU heads"
            value={buHeadFilterSel}
            options={buHeadOptions}
            onChange={(sel: any) => {
              setBuHeadFilterSel(sel);
              setActiveBox(null);
            }}
          />
        </div>
      </div>

      {loading ? (
        <p className="mt-4">Loading…</p>
      ) : (
        <>
          {/* ---- headline numbers ---- */}
          {/* The blueprint's designation-level status: how many roles are covered, in
              progress, or at risk — plus the "% of Critical Roles Covered" metric. */}
          <div className="dash-tiles mt-4">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#e7eefb", color: "#1853a2" }}>
                <FiGrid size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{roleStats.length}</span>
                <span className="stat-label">Critical Roles</span>
                <span className="stat-sub">{scoped.length} successors named</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#e2f2e9", color: "#1f9254" }}>
                <FiCheckCircle size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">
                  {covered}
                  <small> / {roleStats.length}</small>
                </span>
                <span className="stat-label">Covered</span>
                <span className="stat-sub">{coveragePct}% have a Ready-Now successor</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#fdf2d8", color: "#b7860b" }}>
                <FiClock size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{inProgress}</span>
                <span className="stat-label">In Progress</span>
                <span className="stat-sub">successors named, none ready yet</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#fbe3e1", color: "#c23b34" }}>
                <FiAlertTriangle size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{atRisk}</span>
                <span className="stat-label">At Risk</span>
                <span className="stat-sub">no successors named</span>
              </div>
            </div>
          </div>

          {/* ---- 9-box ---- */}
          <h5 className="mt-4">
            9-Box Grid<span className="count-chip">{plotted.length}</span>
          </h5>
          <p className="muted">
            How many successors sit in each Performance × Potential box
            {selectedRole ? " for this role" : " across all critical roles"}. Click a box to
            see who is in it.
          </p>

          <div className="ninebox">
            <div className="nb-corner" />
            {PERFORMANCE_COLS.map((c) => (
              <div className="nb-colhead" key={c.score}>
                {c.label}
              </div>
            ))}

            {POTENTIAL_ROWS.map((row) => (
              // Fragment needs the key, and the <> shorthand can't take one.
              <Fragment key={`row-${row.score}`}>
                <div className="nb-rowhead">{row.label}</div>
                {PERFORMANCE_COLS.map((col) => {
                  const box = row.score * 3 - (3 - col.score);
                  const n = countIn(col.score, row.score);
                  return (
                    <div
                      key={`${row.score}-${col.score}`}
                      className={[
                        "nb-cell",
                        `nb-${bandOf(box)}`,
                        n > 0 ? "has-people" : "",
                        activeBox === box ? "active" : "",
                      ].join(" ")}
                      onClick={() => setActiveBox(n > 0 && activeBox !== box ? box : null)}
                      title={
                        n > 0
                          ? `${n} in ${BOX_CATEGORY[box]} — click to see who`
                          : `No successors in ${BOX_CATEGORY[box]}`
                      }
                    >
                      <span className="nb-count">{n}</span>
                      <span className="nb-cat">{BOX_CATEGORY[box]}</span>
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>

          {unplotted > 0 && (
            <p className="empty-note">
              <b>{unplotted}</b> mapped successor{unplotted === 1 ? "" : "s"} not on the grid —
              they need both a Performance and a Potential rating. Assess them in{" "}
              <b>Development Plan</b>.
            </p>
          )}

          {/* ---- drill-down ---- */}
          {activeBox && (
            <div className="readable-table mt-3">
              <h5>
                {BOX_CATEGORY[activeBox]} — Box {activeBox}
                <span className="count-chip">{drill.length}</span>
              </h5>
              <div className="table table-responsive">
                <table className="table-main table table-sm mt-2">
                  <thead className="table-head">
                    <tr className="table-head-row">
                      <th className="table-head-row">Employee</th>
                      <th className="table-head-row">Employee ID</th>
                      <th className="table-head-row">Critical Role</th>
                      <th className="table-head-row">Pre KRA Score</th>
                      <th className="table-head-row">Final KRA Score</th>
                      <th className="table-head-row">Performance</th>
                      <th className="table-head-row">Potential</th>
                      <th className="table-head-row">Readiness</th>
                      <th className="table-head-row">Feedback</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {drill.map((c) => (
                      <tr className="table-body-row" key={c._id}>
                        <td>{empName(c.employee)}</td>
                        <td>{c.employee?.employeeId || "-"}</td>
                        <td>
                          {c.criticalRole?.designation?.title || "-"}
                          {c.criticalRole?.client?.clientName
                            ? ` — ${c.criticalRole.client.clientName}`
                            : ""}
                        </td>
                        <td>{c.preScore ?? "Not scored yet"}</td>
                        <td>{c.finalScore ?? "Not scored yet"}</td>
                        <td>{c.performance || "-"}</td>
                        <td>{c.potential || "-"}</td>
                        <td>{c.readiness || "-"}</td>
                        <td>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <button
                              className="btn cancel-btn buttonsize compact-btn"
                              onClick={() => openDetails(c)}
                            >
                              View
                            </button>
                            <button
                              className="cd-eye-btn"
                              title="Download report"
                              disabled={!!reportId}
                              onClick={() => downloadReport(c._id)}
                              style={{ cursor: reportId ? "default" : "pointer" }}
                            >
                              <FiDownload size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ---- designation-level status (her Risk Dashboard sheet) ---- */}
          <div className="readable-table mt-4">
            <h5>
              Risk Dashboard<span className="count-chip">{roleStats.length}</span>
            </h5>
            <p className="muted">
              Where each seat stands: how many successors it has, and how many could step in
              today.
            </p>

            {roleStats.length > 0 ? (
              <div className="table table-responsive">
                <table className="table-main table table-sm mt-2">
                  <thead className="table-head">
                    <tr className="table-head-row">
                      <th className="table-head-row">Critical Role</th>
                      <th className="table-head-row">Client</th>
                      <th className="table-head-row">No. of Successors</th>
                      <th className="table-head-row">Ready Now</th>
                      <th className="table-head-row">Risk Level</th>
                      <th className="table-head-row">Status</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {roleStats.map((s) => (
                      <tr className="table-body-row" key={s.role._id}>
                        <td>{s.role.designation?.title || "-"}</td>
                        <td>{s.role.client?.clientName || "-"}</td>
                        <td>{s.successors}</td>
                        <td>{s.readyNow}</td>
                        <td>{s.role.riskLevel || "-"}</td>
                        <td>
                          <span className={`state-pill state-${s.state.replace(/\s+/g, "-").toLowerCase()}`}>
                            {s.state}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-note">No critical roles identified yet.</p>
            )}
          </div>
        </>
      )}

      {/* Feedback detail modal — progress (§C) + the 5 qualitative boxes (§B) */}
      <ModalComponents
        show={!!detailCand}
        onHide={() => setDetailCand(null)}
        title={detailCand ? `Feedback — ${empName(detailCand.employee)}` : "Feedback"}
        modalBody={detailCand ? renderFeedbackModal(detailCand, qualMap[detailCand._id]) : null}
        className="modal-lg"
      />
    </MainCard>
  );
};

export default Dashboard;
